import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { getActivities, getSessionsForActivity, createSession, updateSession, provisionUser } from '../../util/api';
import { logoutThunk } from '../../ducks/auth.duck';

// ─── Async Thunks ───

const fetchActivitiesThunk = createAsyncThunk(
  'CreateSessionPage/fetchActivities',
  async (_, { rejectWithValue, dispatch }) => {
    try {
      const r = await getActivities();
      return r.data || [];
    } catch (e: any) {
      if (e.status === 401) {
        dispatch(logoutThunk());
        return rejectWithValue('Session expired. Please log in again.');
      }
      if (e.status === 403) {
        try {
          await provisionUser();
          const retry = await getActivities();
          return retry.data || [];
        } catch (retryErr: any) {
          return rejectWithValue(retryErr.message || 'Failed after RBAC provisioning');
        }
      }
      return rejectWithValue(e.message || 'Failed to load activities');
    }
  }
);

const createSessionThunk = createAsyncThunk(
  'CreateSessionPage/createSession',
  async (body: any, { rejectWithValue }) => {
    try {
      const r = await createSession(body);
      return r.data || r;
    } catch (e: any) {
      return rejectWithValue(e.message || 'Failed to create session');
    }
  }
);

const updateSessionThunk = createAsyncThunk(
  'CreateSessionPage/updateSession',
  async (body: any, { rejectWithValue }) => {
    try {
      const r = await updateSession(body);
      return r.data || r;
    } catch (e: any) {
      return rejectWithValue(e.message || 'Failed to update session');
    }
  }
);

const fetchEditSessionThunk = createAsyncThunk(
  'CreateSessionPage/fetchEditSession',
  async ({ activityId, sessionId }: { activityId: string; sessionId: string }, { rejectWithValue }) => {
    try {
      const r = await getSessionsForActivity(activityId);
      const sessions = r.data || [];
      const session = sessions.find((s: any) => s.id === sessionId);
      if (!session) return rejectWithValue('Session not found');
      return session;
    } catch (e: any) {
      return rejectWithValue(e.message || 'Failed to load session');
    }
  }
);

// ─── Slice ───

const slice = createSlice({
  name: 'CreateSessionPage',
  initialState: {
    activities: [] as any[],
    activitiesLoading: false,
    activitiesError: null as string | null,
    submitting: false,
    submitError: null as string | null,
    editSession: null as any,
    editSessionLoading: false,
    editSessionError: null as string | null,
  },
  reducers: {},
  extraReducers: builder => {
    builder
      .addCase(fetchActivitiesThunk.pending, s => { s.activitiesLoading = true; s.activitiesError = null; })
      .addCase(fetchActivitiesThunk.fulfilled, (s, a) => { s.activitiesLoading = false; s.activities = a.payload; })
      .addCase(fetchActivitiesThunk.rejected, (s, a) => { s.activitiesLoading = false; s.activitiesError = a.payload as string; })

      .addCase(createSessionThunk.pending, s => { s.submitting = true; s.submitError = null; })
      .addCase(createSessionThunk.fulfilled, s => { s.submitting = false; })
      .addCase(createSessionThunk.rejected, (s, a) => { s.submitting = false; s.submitError = a.payload as string; })

      .addCase(updateSessionThunk.pending, s => { s.submitting = true; s.submitError = null; })
      .addCase(updateSessionThunk.fulfilled, s => { s.submitting = false; })
      .addCase(updateSessionThunk.rejected, (s, a) => { s.submitting = false; s.submitError = a.payload as string; })

      .addCase(fetchEditSessionThunk.pending, s => { s.editSessionLoading = true; s.editSessionError = null; s.editSession = null; })
      .addCase(fetchEditSessionThunk.fulfilled, (s, a) => { s.editSessionLoading = false; s.editSession = a.payload; })
      .addCase(fetchEditSessionThunk.rejected, (s, a) => { s.editSessionLoading = false; s.editSessionError = a.payload as string; });
  },
});

export default slice.reducer;

// ─── Exported Thunks ───

export const fetchActivities = () => (dispatch: any) =>
  dispatch(fetchActivitiesThunk()).unwrap();

export const submitSession = (body: any) => (dispatch: any) =>
  dispatch(createSessionThunk(body)).unwrap();

export const submitUpdateSession = (body: any) => (dispatch: any) =>
  dispatch(updateSessionThunk(body)).unwrap();

export const fetchEditSession = (activityId: string, sessionId: string) => (dispatch: any) =>
  dispatch(fetchEditSessionThunk({ activityId, sessionId })).unwrap();

// ─── loadData ───

export const loadData = () => (dispatch: any) => dispatch(fetchActivitiesThunk());

// ─── Selectors ───

export const selectActivities = (s: any) => s.CreateSessionPage?.activities || [];
export const selectActivitiesLoading = (s: any) => s.CreateSessionPage?.activitiesLoading || false;
export const selectSubmitting = (s: any) => s.CreateSessionPage?.submitting || false;
export const selectEditSession = (s: any) => s.CreateSessionPage?.editSession || null;
export const selectEditSessionLoading = (s: any) => s.CreateSessionPage?.editSessionLoading || false;
